import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/index.css";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServicePage from "@/pages/ServicePage";
import Portfolio from "@/pages/Portfolio";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Mockup from "@/pages/Mockup";
import MockupThankYou from "@/pages/MockupThankYou";
import ResidentialTintLP from "@/pages/ResidentialTintLP";
import CommercialWrapsLP from "@/pages/CommercialWrapsLP";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import { AuthProvider } from "@/lib/auth";
import { captureAttribution, installClickTracker, trackEvent } from "@/lib/analytics";

// Module-scope path guard is StrictMode-immune. On first mount (both simulated runs
// see the same pathname) → no fire because path matches. On genuine route change →
// pathname differs from stored → fire once and update.
let lastFiredPixelPath = null;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    captureAttribution(); // must run on the very first landing — ad clicks arrive here
    // Initial mount: base Meta Pixel snippet in index.html already fired PageView, and
    // trackEvent's dataLayer/GA4/pixel fires should also happen exactly once. Skip
    // duplicate invocations (StrictMode remounts on the same path) by comparing pathname.
    if (lastFiredPixelPath === pathname) return;
    const isInitial = lastFiredPixelPath === null;
    lastFiredPixelPath = pathname;
    trackEvent("page_view", { path: pathname });
    // On the initial mount the base pixel already fired PageView, so skip the manual one.
    if (isInitial) return;
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <StickyMobileCTA />
    </>
  );
}

function AdminShell({ children }) {
  return <main>{children}</main>;
}

export default function App() {
  useEffect(() => { installClickTracker(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
            <Route path="/services/:slug" element={<PublicLayout><ServicePage /></PublicLayout>} />
            <Route path="/portfolio" element={<PublicLayout><Portfolio /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/mockup" element={<PublicLayout><Mockup /></PublicLayout>} />
            <Route path="/mockup/thank-you" element={<PublicLayout><MockupThankYou /></PublicLayout>} />
            {/* Meta Ads landing pages — no nav/footer, minimal layout, not linked from site */}
            <Route path="/lp/residential-tint" element={<ResidentialTintLP />} />
            <Route path="/lp/commercial-wraps" element={<CommercialWrapsLP />} />
            <Route path="/admin-login" element={<AdminShell><AdminLogin /></AdminShell>} />
            <Route path="/admin" element={<AdminShell><AdminDashboard /></AdminShell>} />
            <Route path="*" element={<PublicLayout><Home /></PublicLayout>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
